import { useEffect, useState } from "react";
import { Phone, MessageCircle, ChevronRight, Loader2, Inbox as InboxIcon, Search } from "lucide-react";
import { api } from "../../lib/api";
import { styleFor } from "../../lib/leadKindStyles";
import { formatPhoneDisplay, telLink, whatsappLink } from "../../lib/phone";
import { formatReceivedAt } from "../../lib/dateFormat";
import LeadDetailModal from "./LeadDetailModal";

const STATUS_BADGE = {
    new: "bg-amber-100 text-amber-800",
    contacted: "bg-blue-100 text-blue-700",
    in_progress: "bg-indigo-100 text-indigo-700",
    converted: "bg-emerald-100 text-emerald-700",
    lost: "bg-slate-100 text-slate-600",
    under_review: "bg-blue-100 text-blue-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
};

const STATUS_FILTER_BUTTONS = [
    { v: "all", l: "All" },
    { v: "new", l: "New" },
    { v: "contacted", l: "Contacted" },
    { v: "in_progress", l: "In progress" },
    { v: "converted", l: "Converted" },
];

export default function MobileInbox({ onCountChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [inq, reg, tut] = await Promise.all([
                api.get("/admin/inquiries"),
                api.get("/admin/registrations"),
                api.get("/admin/tutor-applications"),
            ]);
            const merged = [
                ...inq.data.map((x) => ({ ...x, _kind: "inquiries" })),
                ...reg.data.map((x) => ({ ...x, _kind: "registrations" })),
                ...tut.data.map((x) => ({ ...x, _kind: "tutor-applications" })),
            ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setItems(merged);
            const newCount = merged.filter((x) => (x.status || "new") === "new").length;
            onCountChange?.(newCount);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filtered = items.filter((it) => {
        if (statusFilter !== "all" && (it.status || "new") !== statusFilter) return false;
        if (search.trim()) {
            const q = search.toLowerCase();
            return [it.name, it.email, it.phone, it.city, it.subject, it.message].some((f) => (f || "").toString().toLowerCase().includes(q));
        }
        return true;
    });

    return (
        <div data-testid="mobile-inbox" className="pb-4">
            <div className="px-4 pt-4 pb-2 sticky top-0 bg-[#F8FAFC] z-10">
                <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search leads…"
                        data-testid="mobile-inbox-search"
                        className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    />
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
                    {STATUS_FILTER_BUTTONS.map((b) => (
                        <button
                            key={b.v}
                            onClick={() => setStatusFilter(b.v)}
                            data-testid={`mobile-inbox-filter-${b.v}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                                statusFilter === b.v ? "bg-[#1E3A8A] text-white" : "bg-white border border-slate-200 text-slate-600"
                            }`}
                        >
                            {b.l}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 px-6 text-slate-400">
                    <InboxIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <div className="text-sm">No leads in this view.</div>
                </div>
            ) : (
                <div className="px-4 space-y-2">
                    {filtered.map((it) => {
                        const ks = styleFor(it._kind);
                        // V154 — tutor-applications aren't read-tracked in this rollout,
                        // so treat them as always "read" to avoid a permanent unread halo.
                        const isUnread = it._kind !== "tutor-applications" && !it.opened_at;
                        return (
                        <button
                            key={`${it._kind}-${it.id}`}
                            onClick={() => setView(it)}
                            data-testid={`mobile-lead-${it.id}`}
                            data-kind={it._kind}
                            data-unread={isUnread ? "true" : "false"}
                            className={`w-full text-left border rounded-2xl p-3.5 active:scale-[0.99] transition-all ${
                                isUnread
                                    ? `bg-amber-50/70 border-amber-300 ${ks.leftBorder} shadow-sm`
                                    : `bg-white border-slate-200 ${ks.leftBorder} ${ks.cardHover}`
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`relative w-10 h-10 rounded-full ${ks.accentBg} ${ks.accentText} flex items-center justify-center font-heading font-bold shrink-0`}>
                                    {(it.name || "?").charAt(0).toUpperCase()}
                                    {isUnread && (
                                        <span
                                            aria-hidden="true"
                                            data-testid={`mobile-lead-unread-dot-${it.id}`}
                                            className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-500 ring-2 ring-white"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className={`font-heading truncate ${isUnread ? "font-bold text-[#0F172A]" : "font-semibold text-slate-800"}`}>{it.name}</div>
                                        <span data-testid={`mobile-kind-badge-${it._kind}`} className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${ks.badge}`}>{ks.short}</span>
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">{formatPhoneDisplay(it.phone)}{it.city ? ` · ${it.city}` : ""}</div>
                                    <div className="text-xs text-slate-500 truncate mt-0.5">{it.subject || it.subjects || it.grade || it.message || "—"}</div>
                                    {it.created_at && (
                                        <div className="text-[10px] text-slate-400 mt-1 truncate" data-testid={`mobile-lead-received-${it.id}`}>
                                            Received: <span className="font-medium text-slate-500">{formatReceivedAt(it.created_at)}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between mt-2">
                                        <div className="flex items-center gap-1.5">
                                            {isUnread && (
                                                <span
                                                    data-testid={`mobile-lead-unread-${it.id}`}
                                                    className="text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-amber-500 text-white"
                                                >
                                                    New
                                                </span>
                                            )}
                                            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${STATUS_BADGE[it.status || "new"]}`}>
                                                {(it.status || "new").replace(/_/g, " ")}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {it.phone && (
                                                <a
                                                    href={telLink(it.phone)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="Call"
                                                    className="p-1.5 rounded-full bg-blue-50 text-[#1E3A8A] hover:bg-blue-100"
                                                >
                                                    <Phone className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                            {it.phone && (
                                                <a
                                                    href={whatsappLink(it.phone)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="WhatsApp"
                                                    className="p-1.5 rounded-full bg-green-50 text-[#25D366] hover:bg-green-100"
                                                >
                                                    <MessageCircle className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                            <ChevronRight className="w-4 h-4 text-slate-300" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </button>
                        );
                    })}
                </div>
            )}

            {view && (
                <LeadDetailModal
                    kind={view._kind}
                    item={view}
                    onClose={() => setView(null)}
                    onSaved={load}
                    onOpened={(id, openedAt) => {
                        // V154 — optimistic parent patch (see LeadsView note).
                        setItems((prev) => prev.map((it) => (
                            it.id === id && it._kind === view._kind
                                ? { ...it, opened_at: openedAt }
                                : it
                        )));
                    }}
                />
            )}
        </div>
    );
}
